/**
 * The Plans list's cascading Service → Client filters (Tali, 2026-10-14,
 * "same as Chase", without Carer: plans aren't looked up by who works them).
 * Pure, so it is testable without a DOM.
 *
 * A service narrows the client dropdown to that service's clients; changing
 * the service clears a client choice that no longer belongs to it. Keys are
 * ids, never names (two clients can share a name).
 */

export interface ListFilterRow {
  docId: string;
  name: string;
  service?: string | null;
}

export interface ListFilterChoice {
  service: string;
  client: string;
}

export interface ListFilterOption {
  key: string;
  label: string;
  count: number;
}

const NO_SERVICE = "__none";

export function serviceKey(r: ListFilterRow): string {
  return r.service || NO_SERVICE;
}

export function passesListFilters(r: ListFilterRow, f: ListFilterChoice): boolean {
  if (f.service && serviceKey(r) !== f.service) return false;
  if (f.client && r.docId !== f.client) return false;
  return true;
}

export function listFilterOptions(
  rows: ListFilterRow[],
  which: keyof ListFilterChoice,
  choice: ListFilterChoice,
): ListFilterOption[] {
  const scope = which === "client" ? rows.filter((r) => passesListFilters(r, { service: choice.service, client: "" })) : rows;
  const by = new Map<string, ListFilterOption>();
  for (const r of scope) {
    const key = which === "service" ? serviceKey(r) : r.docId;
    const label = which === "service" ? r.service || "No service recorded" : r.name;
    const o = by.get(key) ?? { key, label, count: 0 };
    o.count += 1;
    by.set(key, o);
  }
  return [...by.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** After a service change, drop a client the new service doesn't contain. */
export function pruneClient(rows: ListFilterRow[], f: ListFilterChoice): ListFilterChoice {
  if (!f.client) return f;
  return listFilterOptions(rows, "client", f).some((o) => o.key === f.client) ? f : { ...f, client: "" };
}
